import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from '../Styles/Reservations.module.css';
import { format, eachDayOfInterval, isPast, isSameDay } from 'date-fns';
import { fi } from 'date-fns/locale';
import { useParams } from 'react-router-dom';
import IconButton from '../Images/IconButton';
import WeekTextBox from '../Components/WeekTextBox';
import ReservationsMobileDayView from '../Components/reservations/ReservationsMobileDayView';
import ReservationsDesktopWeekView from '../Components/reservations/ReservationsDesktopWeekView';
import ReservationPopup from '../Components/reservations/ReservationPopup';
import { useUsers } from '../Commoncontext/UsersContext';
import { useSelectedCompany } from '../Commoncontext/SelectedCompanyContext';
import { useCompany } from '../Commoncontext/CompanyContext';
import { useVarauskalenterit } from '../Commoncontext/VarauskalenteritContext';
import { useReservations } from '../Commoncontext/ReservationsContext';
import { SLOT_TAKEN } from '../Api/ReservationsApi';
import { VIEW_BREAKPOINTS } from '../Config/responsive';
import useResponsive from '../hooks/useResponsive';
import {
  dayKey,
  isSlotBlockedBy,
} from '../Utils/ReservationsUtil';

// Näytetään UI:ssa tunnit 6–21 (16 slottia)
const HOURS = Array.from({ length: 16 }, (_, i) => 6 + i);

export default function ReservationsNew() {
  const { calendarSlug } = useParams();
  const slugToLabel = { etatyotila: 'Etätyötila', saunatila: 'Sauna' };
  const { width } = useResponsive();
  const isCompactReservationsView = width <= VIEW_BREAKPOINTS.reservationsWeekGridMin;
  const { user } = useUsers();
  const { selectedCompanyId } = useSelectedCompany();
  const { companyNamesById } = useCompany();
  const {
    calendars,
    loading: calendarsLoading,
    companyColors,
    ensureCompanyColor,
    getSlotConfig,
    getCalendarByIdCached,
  } = useVarauskalenterit();

  const {
    activeCalendarId,
    setActiveCalendarId,
    weekStart,
    weekEnd,
    nextWeek,
    prevWeek,
    reservationsByDay,
    book,
    cancel,
    loading: reservationsLoading,
  } = useReservations();

  const [selectedSlot, setSelectedSlot] = useState(null);
  const [popError, setPopError] = useState('');
  const popupRef = useRef(null);
  const [blocks, setBlocks] = useState([]);
  const [cancelLimitHours, setCancelLimitHours] = useState(0);
  const [maxPerWeekLimit, setMaxPerWeekLimit] = useState(0);
  const [selectedMobileDay, setSelectedMobileDay] = useState(null);

  useEffect(() => {
    if (!calendars || calendars.length === 0) return;
    if (calendarSlug) {
      const wantedLabel = slugToLabel[calendarSlug.toLowerCase()];
      const byType = wantedLabel
        ? calendars.find(
            (c) => (c.tyyppi || '').toLowerCase() === wantedLabel.toLowerCase()
          )
        : null;
      const byName = !byType
        ? calendars.find((c) =>
            (c.nimi || '').toLowerCase().includes(calendarSlug.toLowerCase())
          )
        : null;
      const chosen = byType || byName || calendars[0];
      if (chosen && chosen.id !== activeCalendarId) {
        setActiveCalendarId(chosen.id);
      }
      return;
    }
    if (!activeCalendarId) {
      setActiveCalendarId(calendars[0].id);
    }
  }, [calendars, calendarSlug, activeCalendarId, setActiveCalendarId]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!activeCalendarId) {
        setBlocks([]);
        setCancelLimitHours(0);
        setMaxPerWeekLimit(0);
        return;
      }
      try {
        const full = await getCalendarByIdCached(activeCalendarId);
        if (ignore) return;
        setBlocks(Array.isArray(full?.sulut) ? full.sulut : []);
        const limit = Number(full?.rajoitukset?.peruutusMaxTuntiAikaraja ?? 0);
        setCancelLimitHours(Number.isFinite(limit) ? Math.max(0, limit) : 0);
        const maxPerWeek = Number(full?.rajoitukset?.maxPerKayttajaPerVko ?? 0);
        setMaxPerWeekLimit(
          Number.isFinite(maxPerWeek) ? Math.max(0, maxPerWeek) : 0
        );
      } catch {
        if (!ignore) {
          setBlocks([]);
          setCancelLimitHours(0);
          setMaxPerWeekLimit(0);
        }
      }
    })();
    return () => {
      ignore = true;
    };
  }, [activeCalendarId, getCalendarByIdCached, weekStart, weekEnd]);

  const weekDays = useMemo(() => {
    if (!weekStart || !weekEnd) return [];
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  }, [weekStart, weekEnd]);

  useEffect(() => {
    if (!weekDays.length) return;
    if (!selectedMobileDay) {
      setSelectedMobileDay(weekDays[0]);
      return;
    }
    const stillExists = weekDays.some((d) => isSameDay(d, selectedMobileDay));
    if (!stillExists) {
      setSelectedMobileDay(weekDays[0]);
    }
  }, [weekDays, selectedMobileDay]);

  const weekLabel = useMemo(() => {
    const s = format(weekStart, 'dd.MM.', { locale: fi });
    const e = format(weekEnd, 'dd.MM.', { locale: fi });
    return `Vko ${format(weekStart, 'I', { locale: fi })}: ${s} - ${e}`;
  }, [weekStart, weekEnd]);

  useEffect(() => {
    function onDown(e) {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setSelectedSlot(null);
        setPopError('');
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  function onClickSlot(d, hour) {
    if (!activeCalendarId) return;
    const blockHit = (blocks || []).find((b) => isSlotBlockedBy(b, d, hour).blocked);
    if (blockHit) return;
    const key = dayKey(d);
    const idx = hour - 6;
    const res = reservationsByDay[key]?.[idx];
    const sc = getSlotConfig(activeCalendarId, d, hour);
    const past = isPast(
      new Date(d.getFullYear(), d.getMonth(), d.getDate(), hour, 0, 0)
    );
    if (!sc.open || past) return;
    if (res && res.id !== user.uid) return;
    setSelectedSlot({ date: d, hour, existing: res || null });
    setPopError('');
  }

  async function confirmBooking() {
    if (!selectedSlot || !activeCalendarId) return;
    const { date, hour } = selectedSlot;
    const { price } = getSlotConfig(activeCalendarId, date, hour);
    try {
      await book({
        date,
        hour,
        user,
        companyId: selectedCompanyId,
        calendarId: activeCalendarId,
        price,
      });
      setSelectedSlot(null);
      setPopError('');
    } catch (e) {
      if (e?.code === SLOT_TAKEN) {
        setPopError('Tämä aika on jo varattu toiselle käyttäjälle.');
      } else {
        setPopError('Varauksen luonti epäonnistui. Yritä uudelleen.');
      }
    }
  }

  async function cancelBooking() {
    if (!selectedSlot) return;
    const { date, hour } = selectedSlot;
    try {
      await cancel({ date, hour, userId: user.uid });
      setSelectedSlot(null);
      setPopError('');
    } catch {
      setPopError('Varauksen peruminen epäonnistui. Yritä uudelleen.');
    }
  }

  useEffect(() => {
    Object.values(reservationsByDay || {}).forEach((dayMap) => {
      Object.values(dayMap || {}).forEach((slot) => {
        if (slot?.talonyhtiöid) ensureCompanyColor(slot.talonyhtiöid);
      });
    });
  }, [reservationsByDay, ensureCompanyColor]);

  const myBookingsThisWeek = useMemo(() => {
    let n = 0;
    Object.values(reservationsByDay || {}).forEach((dayMap) => {
      Object.values(dayMap || {}).forEach((slot) => {
        if (slot?.id === user?.uid) n += 1;
      });
    });
    return n;
  }, [reservationsByDay, user?.uid]);

  const weeklyLimitReached = useMemo(() => {
    const limit = Number(maxPerWeekLimit || 0);
    if (!Number.isFinite(limit) || limit <= 0) return false;
    return myBookingsThisWeek >= limit;
  }, [myBookingsThisWeek, maxPerWeekLimit]);

  const companiesInWeek = useMemo(() => {
    const set = new Set();
    Object.values(reservationsByDay || {}).forEach((dayMap) => {
      Object.values(dayMap || {}).forEach((slot) => {
        if (slot?.talonyhtiöid) {
          set.add(slot.talonyhtiöid);
        }
      });
    });
    return Array.from(set);
  }, [reservationsByDay]);

  const isLoading = calendarsLoading || reservationsLoading;

  return (
    <main className={styles.content}>
      {!calendarSlug && (
        <div className={styles.calendarPicker}>
          <select
            id="calendarSelect"
            className={styles.dropdown}
            value={activeCalendarId || ''}
            onChange={(e) => setActiveCalendarId(e.target.value)}
            disabled={calendarsLoading || calendars.length === 0}
          >
            {calendars.length === 0 ? (
              <option value="">Ei kalentereita</option>
            ) : (
              calendars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nimi || c.id}
                </option>
              ))
            )}
          </select>
        </div>
      )}
      {!isCompactReservationsView && (
        <div className={styles.weekselector}>
          <div style={{ marginRight: '10px' }}>
            <IconButton direction="left" onClick={prevWeek} />
          </div>
          <WeekTextBox text={weekLabel} />
          <div style={{ marginLeft: '10px' }}>
            <IconButton direction="right" onClick={nextWeek} />
          </div>
        </div>
      )}
      {isLoading ? (
        <p></p>
      ) : isCompactReservationsView ? (
        <ReservationsMobileDayView
          weekLabel={weekLabel}
          prevWeek={prevWeek}
          nextWeek={nextWeek}
          weekDays={weekDays}
          selectedMobileDay={selectedMobileDay}
          setSelectedMobileDay={setSelectedMobileDay}
          hours={HOURS}
          reservationsByDay={reservationsByDay}
          getSlotConfig={getSlotConfig}
          activeCalendarId={activeCalendarId}
          blocks={blocks}
          user={user}
          companyColors={companyColors}
          onClickSlot={onClickSlot}
          companiesInWeek={companiesInWeek}
          companyNamesById={companyNamesById}
        />
      ) : (
        <ReservationsDesktopWeekView
          weekDays={weekDays}
          hours={HOURS}
          reservationsByDay={reservationsByDay}
          blocks={blocks}
          getSlotConfig={getSlotConfig}
          activeCalendarId={activeCalendarId}
          companyColors={companyColors}
          user={user}
          onClickSlot={onClickSlot}
        />
      )}
      {!isCompactReservationsView && companiesInWeek.length > 0 && (
        <div className={styles.legendWrapper}>
          <div className={styles.companyLegend}>
            {companiesInWeek.map((companyId) => (
              <div key={companyId} className={styles.legendItem}>
                <span
                  className={styles.legendColor}
                  style={{ backgroundColor: companyColors[companyId] }}
                />
                <span className={styles.legendText}>
                  {companyNamesById?.[companyId] ||
                    user?.companyNames?.[companyId] ||
                    'Taloyhtiö'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <ReservationPopup
        popupRef={popupRef}
        selectedSlot={selectedSlot}
        activeCalendarId={activeCalendarId}
        getSlotConfig={getSlotConfig}
        cancelLimitHours={cancelLimitHours}
        popError={popError}
        user={user}
        weeklyLimitReached={weeklyLimitReached}
        maxPerWeekLimit={maxPerWeekLimit}
        onCancelBooking={cancelBooking}
        onConfirmBooking={confirmBooking}
      />
    </main>
  );
}